/**
 * Represents a hexadecimal color value.
 *
 * Valid formats:
 * - 3-digit: #RGB (e.g., '#fff', '#F00')
 * - 4-digit: #RGBA (e.g., '#ffff', '#F00A')
 * - 6-digit: #RRGGBB (e.g., '#ffffff', '#FF0000')
 * - 8-digit: #RRGGBBAA (e.g., '#ffffff80', '#FF0000FF').
 *
 * Use {@link isValidHexColor} to validate hex color strings at runtime.
 *
 * @example '#fff'      // 3-digit
 * @example '#ffff'     // 4-digit with alpha
 * @example '#FFFFFF'   // 6-digit
 * @example '#FFFFFF80' // 8-digit with alpha
 */
export type HexColor = `#${string}`;

/**
 * Represents hex color values for color swatches and gradients.
 *
 * - Single HexColor: Solid color (e.g., "#FF0000").
 * - Array of 2 HexColors: Two-color gradient (e.g., ["#FF0000", "#0000FF"]).
 *
 * UI typically renders arrays as diagonal split gradients.
 */
export type SwatchHexValue = HexColor | readonly [HexColor, HexColor];

/**
 * Normalizes a hex color to lowercase 6-digit format.
 *
 * Handles various hex color formats:
 * - 3-digit: #fff → #ffffff
 * - 6-digit: #FFFFFF → #ffffff
 * - Mixed case: #FfFfFf → #ffffff.
 *
 * @param hex - The hex color to normalize (e.g., '#fff', '#FFFFFF').
 * @returns Normalized hex color in lowercase 6-digit format (e.g., '#ffffff').
 *
 * @example
 * normalizeHexColor('#fff')     // => '#ffffff'
 * normalizeHexColor('#FFFFFF')  // => '#ffffff'
 * normalizeHexColor('#FfF')     // => '#ffffff'
 */
export function normalizeHexColor(hex: HexColor): HexColor {
  const normalized = hex.toLowerCase();

  // If 3-digit hex (#fff), expand to 6-digit (#ffffff)
  if (normalized.length === 4) {
    const [, r, g, b] = normalized;
    return `#${r}${r}${g}${g}${b}${b}` as HexColor;
  }

  return normalized as HexColor;
}

/**
 * Checks if a hex color represents white.
 *
 * Compares against normalized white (#ffffff) to handle various formats:
 * - #fff, #FFF, #ffffff, #FFFFFF all return true.
 *
 * @param hex - The hex color to check.
 * @returns True if the color is white, false otherwise.
 *
 * @example
 * isWhiteHexColor('#fff')      // => true
 * isWhiteHexColor('#FFFFFF')   // => true
 * isWhiteHexColor('#000')      // => false
 */
export function isWhiteHexColor(hex: HexColor): boolean {
  return normalizeHexColor(hex) === '#ffffff';
}

/**
 * Validates if a string is a valid hex color format.
 *
 * Accepts 3, 4, 6, or 8-digit hex colors with # prefix.
 * Valid formats: #RGB, #RGBA, #RRGGBB, or #RRGGBBAA (case-insensitive).
 *
 * @param value - The value to validate as a hex color.
 * @returns True if the value is a valid hex color, false otherwise.
 *
 * @example
 * isValidHexColor('#fff')      // => true (3-digit)
 * isValidHexColor('#ffff')     // => true (4-digit with alpha)
 * isValidHexColor('#FFFFFF')   // => true (6-digit)
 * isValidHexColor('#FFFFFF80') // => true (8-digit with alpha)
 * isValidHexColor('fff')       // => false (missing #)
 * isValidHexColor('#gg0000')   // => false (invalid hex char)
 * isValidHexColor('#ff')       // => false (invalid length)
 * isValidHexColor(null)        // => false
 * isValidHexColor(undefined)   // => false
 */
export function isValidHexColor(value: unknown): value is HexColor {
  if (typeof value !== 'string') {
    return false;
  }

  // Must start with # and be 4, 5, 7, or 9 characters (#RGB, #RGBA, #RRGGBB, #RRGGBBAA)
  const validLengths = [4, 5, 7, 9];
  if (!value.startsWith('#') || !validLengths.includes(value.length)) {
    return false;
  }

  // Check if all characters after # are valid hex digits (0-9, A-F, a-f)
  const hexDigits = value.slice(1);
  return /^[0-9A-Fa-f]+$/.test(hexDigits);
}
